import React from 'react';
import styles from '../css/vdr.module.css';

/**
 * Folder + file-type + URL icons matching Figma VDR Folder view (6971:15290).
 * Folders: grey outline folder in a muted square.
 * Files: colored extension badges (PDF / XLS / ZIP / …).
 * URL links: chain-link glyph in the same muted square (API: document_link, no document blob).
 */

const FOLDER_SVG = (
    <svg width="14" height="12" viewBox="0 0 14.2969 11.6953" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path
            d="M0.893556 3.16698C0.893556 2.42533 1.49224 1.82665 2.23389 1.82665H5.80812L7.14845 3.61376H12.063C12.8047 3.61376 13.4033 4.21244 13.4033 4.95409V9.86865C13.4033 10.6103 12.8047 11.209 12.063 11.209H2.23389C1.49224 11.209 0.893556 10.6103 0.893556 9.86865V3.16698Z"
            fill="#003048"
            fillOpacity="0.19"
            stroke="#003048"
            strokeOpacity="0.376"
            strokeWidth="0.714845"
        />
    </svg>
);

/** Chain-link glyph — same visual weight as the Figma folder icon */
const LINK_SVG = (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path
            d="M5.95 8.05a2.8 2.8 0 0 0 3.96 0l1.75-1.75a2.8 2.8 0 1 0-3.96-3.96L7 3.04"
            stroke="#c89741"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M8.05 5.95a2.8 2.8 0 0 0-3.96 0L2.34 7.7a2.8 2.8 0 1 0 3.96 3.96L7 10.96"
            stroke="#c89741"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const VDRFolderIcon = ({ className, size = 26 }) => (
    <span
        className={`${styles.folderIconBox} ${className || ''}`}
        style={size !== 26 ? { width: size, height: size } : undefined}
        aria-hidden
    >
        {FOLDER_SVG}
    </span>
);

/** URL / external link icon — same muted 26×26 square as folders, purple link glyph */
export const VDRLinkIcon = ({ className, size = 26 }) => (
    <span
        className={`${styles.folderIconBox} ${styles.linkIconBox} ${className || ''}`}
        style={size !== 26 ? { width: size, height: size } : undefined}
        aria-hidden
        title="URL link"
    >
        {LINK_SVG}
    </span>
);

const EXT_META = {
    pdf: { label: 'PDF', tone: 'pdf' },
    doc: { label: 'DOC', tone: 'doc' },
    docx: { label: 'DOC', tone: 'doc' },
    xls: { label: 'XLS', tone: 'xls' },
    xlsx: { label: 'XLS', tone: 'xls' },
    csv: { label: 'CSV', tone: 'xls' },
    ppt: { label: 'PPT', tone: 'ppt' },
    pptx: { label: 'PPT', tone: 'ppt' },
    zip: { label: 'ZIP', tone: 'zip' },
    rar: { label: 'ZIP', tone: 'zip' },
    '7z': { label: 'ZIP', tone: 'zip' },
    txt: { label: 'TXT', tone: 'default' },
    md: { label: 'MD', tone: 'default' },
    png: { label: 'IMG', tone: 'default' },
    jpg: { label: 'IMG', tone: 'default' },
    jpeg: { label: 'IMG', tone: 'default' },
    gif: { label: 'IMG', tone: 'default' },
    webp: { label: 'IMG', tone: 'default' },
    mp4: { label: 'VID', tone: 'default' },
    mov: { label: 'VID', tone: 'default' },
};

const TONE_CLASS = {
    pdf: styles.extBadgePdf,
    xls: styles.extBadgeXls,
    zip: styles.extBadgeZip,
    doc: styles.extBadgeDoc,
    ppt: styles.extBadgePpt,
    default: styles.extBadgeDefault,
};

export const normalizeExtension = extension => {
    const raw = String(extension || '')
        .split('?')[0]
        .replace(/^\./, '')
        .toLowerCase();
    if (!raw) return '';
    const parts = raw.split('.');
    return parts[parts.length - 1] || raw;
};

export function fileTypeLabel(extension) {
    const ext = normalizeExtension(extension);
    const meta = EXT_META[ext];
    if (meta) return meta.label;
    return (ext || 'FILE').slice(0, 4).toUpperCase();
}

/**
 * True when the document is a URL link (Add Link flow), not an uploaded blob.
 * Matches Dataroom2.0: document_link present and no file `document` blob.
 */
export const isVdrLinkItem = file => {
    if (!file || typeof file !== 'object') return false;
    const link = file.document_link || file.link_url || file.url;
    if (!link || typeof link !== 'string' || !link.trim()) return false;
    // Uploaded files may still carry a resolved URL on `document`; treat pure link rows as link-only
    if (file.document) return false;
    return true;
};

/** Colored extension badge — Figma FileIcon (26×26 rounded square + label). */
export const VDRFileTypeIcon = ({ extension, className }) => {
    const ext = normalizeExtension(extension);
    const meta = EXT_META[ext] || {
        label: (ext || 'FILE').slice(0, 4).toUpperCase(),
        tone: 'default',
    };
    return (
        <span className={`${styles.extBadge} ${TONE_CLASS[meta.tone] || styles.extBadgeDefault} ${className || ''}`} aria-hidden>
            {meta.label}
        </span>
    );
};

/**
 * Shared row icon: URL link → chain icon; otherwise extension badge.
 * Pass the full file/document object so document_link is detected.
 */
export const VDRDocumentIcon = ({ file, extension, className }) => {
    if (isVdrLinkItem(file)) {
        return <VDRLinkIcon className={className} />;
    }
    const ext =
        extension ||
        file?.extension ||
        file?.document_name ||
        file?.name ||
        file?.file_name ||
        '';
    return <VDRFileTypeIcon extension={ext} className={className} />;
};

export default VDRFileTypeIcon;
